import { Link, useRouter } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';

export interface RouteErrorFallbackProps {
  /** Whatever was thrown. TanStack Router types it as `unknown`, so non-Error values are accepted too. */
  error: unknown;
  /** Whether the app is running in development mode. When true, shows the error message. */
  isDev?: boolean;
  /** Optional callback from TanStack Router's ErrorComponentProps. Called alongside router.invalidate() on retry. */
  onReset?: () => void;
}

export function RouteErrorFallback({ error, isDev = false, onReset }: RouteErrorFallbackProps): React.JSX.Element {
  const { t } = useTranslation();
  const router = useRouter();

  useEffect((): void => {
    if (isDev) {
      console.error(error);
    }
  }, [error, isDev]);

  const handleRetry = (): void => {
    onReset?.();
    void router.invalidate();
  };

  return (
    <Card className="mx-auto max-w-lg">
      <CardHeader>
        <div className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <CardTitle>{t('errors.route.title')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">{t('errors.route.description')}</p>
        {isDev && (
          <pre className="overflow-auto rounded bg-destructive/10 p-4 text-start text-sm text-destructive">
            {error instanceof Error ? error.message : String(error)}
          </pre>
        )}
        <div className="flex gap-2">
          <Button onClick={handleRetry} variant="outline">
            {t('errors.retry')}
          </Button>
          <Button asChild>
            <Link to="/">{t('errors.goHome')}</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
